import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

const baseField =
  'w-full bg-white border border-zinc-200 text-zinc-900 placeholder:text-zinc-400 ' +
  'rounded-lg shadow-sm transition-[border-color,box-shadow] ' +
  'focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 ' +
  'disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(baseField, 'h-10 px-3 text-sm', className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(baseField, 'px-3 py-2.5 text-sm leading-relaxed', className)}
        {...props}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(baseField, 'h-10 px-3 text-sm cursor-pointer bg-white', className)}
        {...props}
      />
    );
  },
);

export function Label({
  children,
  htmlFor,
  hint,
  className,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  className?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('flex items-center justify-between text-[13px] font-medium text-zinc-700 mb-1.5', className)}
    >
      <span>{children}</span>
      {hint && <span className="text-[12px] font-normal text-zinc-400">{hint}</span>}
    </label>
  );
}
